// CartContext.jsx
// Manages the shopping cart state: items, quantities, totals.
// Cart is persisted to localStorage and Firestore backend (if logged in).

import React, { createContext, useContext, useReducer, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";

const CartContext = createContext();

// Custom hook – call useCart() in any component
export function useCart() {
  return useContext(CartContext);
}

// ── Reducer ────────────────────────────────────────────────────────────────────
// Handles all cart mutations in a predictable, testable way.
function cartReducer(state, action) {
  switch (action.type) {
    case "ADD_ITEM": {
      const existing = state.items.find((i) => i.id === action.payload.id);
      if (existing) {
        // Item already in cart – increment quantity
        return {
          ...state,
          items: state.items.map((i) =>
            i.id === action.payload.id
              ? { ...i, quantity: i.quantity + 1 }
              : i
          ),
        };
      }
      // New item – add with quantity 1
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }],
      };
    }

    case "REMOVE_ITEM":
      return {
        ...state,
        items: state.items.filter((i) => i.id !== action.payload),
      };

    case "UPDATE_QUANTITY": {
      const { id, quantity } = action.payload;
      if (quantity <= 0) {
        // Quantity reached zero – remove item
        return { ...state, items: state.items.filter((i) => i.id !== id) };
      }
      return {
        ...state,
        items: state.items.map((i) =>
          i.id === id ? { ...i, quantity } : i
        ),
      };
    }

    case "CLEAR_CART":
      return { ...state, items: [] };

    case "LOAD_CART":
      return { ...state, items: action.payload };

    default:
      return state;
  }
}

// ── Provider ───────────────────────────────────────────────────────────────────
export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  const [isLoaded, setIsLoaded] = useState(false);
  const { currentUser } = useAuth(); // Safe because CartProvider is inside AuthProvider

  // 1) Load cart from localStorage or Backend on first render / user change
  useEffect(() => {
    async function loadInitialCart() {
      let loadedItems = [];
      let foundBackendSync = false;

      // Try fetching from the backend first if logged in
      if (currentUser) {
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const snap = await getDoc(userDocRef);
          if (snap.exists() && snap.data().cart) {
            loadedItems = snap.data().cart;
            foundBackendSync = true;
          }
        } catch (err) {
          console.error("Failed to load cart from DB:", err);
        }
      }
      
      // If no backend cart exists (or not logged in), check localStorage
      if (!foundBackendSync) {
        const saved = localStorage.getItem("giftcraft_cart");
        if (saved) {
          loadedItems = JSON.parse(saved);
        }
      }

      dispatch({ type: "LOAD_CART", payload: loadedItems });
      setIsLoaded(true);
    }
    
    // We intentionally load the cart when the provider mounts, 
    // and whenever the authenticated user explicitly changes (login/logout context)
    loadInitialCart();
  }, [currentUser]);

  // 2) Save cart to localStorage and Backend whenever items change
  useEffect(() => {
    // Crucial strictly guard against overwriting data with empty initial state 
    // before the LOAD phase completes.
    if (!isLoaded) return; 

    // Synchronize to localStorage
    localStorage.setItem("giftcraft_cart", JSON.stringify(state.items));

    // Synchronize to Firestore backend for future retrieval
    if (currentUser) {
      const userDocRef = doc(db, "users", currentUser.uid);
      updateDoc(userDocRef, { cart: state.items }).catch((err) => {
        console.error("Failed to sync cart to DB:", err);
      });
    }
  }, [state.items, isLoaded, currentUser]);

  // ── Derived values ──────────────────────────────────────────────────────────
  // Total number of individual items (sum of quantities)
  const cartCount = state.items.reduce((sum, i) => sum + i.quantity, 0);

  // Subtotal before shipping & tax
  const subtotal = state.items.reduce(
    (sum, i) => sum + i.price * i.quantity,
    0
  );

  // ── Helper actions ──────────────────────────────────────────────────────────
  const addToCart = (product) =>
    dispatch({ type: "ADD_ITEM", payload: product });

  const removeFromCart = (id) =>
    dispatch({ type: "REMOVE_ITEM", payload: id });

  const updateQuantity = (id, quantity) =>
    dispatch({ type: "UPDATE_QUANTITY", payload: { id, quantity } });

  const clearCart = () => dispatch({ type: "CLEAR_CART" });

  const value = {
    cartItems: state.items,
    cartCount,
    subtotal,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
